import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, Input, Badge, Skeleton } from '@bloomcode/ui';
import { useListPostsQuery, type ListPostsQuery, type PostStatus } from '@bloomcode/graphql';
import { formatDate } from '@bloomcode/utils';
import { useCookieSession } from '@bloomcode/auth';
import { useDebounce } from '@bloomcode/hooks';

type PostItem = ListPostsQuery['listPosts']['data'][number];

export default function ContentListPage() {
  const { session } = useCookieSession();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const limit = 12;

  const { data, loading, error, refetch } = useListPostsQuery({
    variables: {
      filters: {
        status: ['PUBLISHED' as PostStatus],
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      },
      pagination: { page, limit },
    },
    fetchPolicy: 'cache-and-network',
    skip: !session,
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const posts: PostItem[] = data?.listPosts?.data ?? [];
  const pagination = data?.listPosts?.pagination;

  return (
    <>
      <Helmet>
        <title>Content | Bloomcode CMS</title>
        <meta name="description" content="Browse all published content" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Content</h1>
          <Input
            placeholder="Search content..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="max-w-md"
          />
        </div>

        {error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-4">Failed to load content: {error.message}</p>
            <button onClick={() => refetch()} className="text-primary underline">
              Retry
            </button>
          </div>
        ) : loading && !posts.length ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : posts.length > 0 ? (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Link key={post.id} to={`/content/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        {post.categories?.[0] && (
                          <Badge variant="secondary">{post.categories[0].name}</Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                      </div>
                      <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {post.excerpt && (
                        <p className="text-muted-foreground line-clamp-3">{post.excerpt}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrev}
                  className="px-4 py-2 text-sm border border-border rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pagination.hasNext}
                  className="px-4 py-2 text-sm border border-border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {debouncedSearch
                ? 'No content found matching your search.'
                : 'No content available yet.'}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
